<template>
  <div
    class="d-flex flex-column flex-md-row flex-md-nowrap"
    style="gap: 8px;"
  >
    <v-col
      md="3"
      cols="12"
      class="d-flex flex-column md-and-up-sticky static"
      order-md="1"
      order="1"
      style="gap: 8px;"
      :style="`top: ${focusModeToolbarTop}; height: fit-content;`"
    >
      <template v-if="isLoading">
        <v-skeleton-loader
          v-for="i in 3"
          :key="i"
          type="card-heading, divider, list-item@4"
          class="mb-1"
        />
      </template>
      <template v-else-if="dataIsReady">
        <List
          :title="references.statistics.total"
          :items="[
            totalApplications,
            totalCanceledApplications,
            totalOffers,
            totalOffersWithoutApplications
          ]"
        />
        <List
          :title="references.statistics.averageTimeToApplyOffer"
          is-inactive
          :items="[
            averageTimeToApplyOffer
          ]"
        />
        <List
          :title="references.statistics.topOffersWithApplications"
          :items="getTopOffersWithApplications"
        />
      </template>
    </v-col>
    <v-col
      cols="12"
      md="6"
      order-md="2"
      order="3"
    >
      <template v-if="isLoading">
        <v-skeleton-loader
          v-for="i in 2"
          :key="i"
          type="card-heading, image@2"
          class="mb-2"
        />
      </template>
      <template v-else-if="dataIsReady">
        <v-row>
          <v-col>
            <v-card
              elevation="0"
              height="500"
              class="pt-5 pa-2"
            >
              <v-subheader
                class="
                  align-start
                  text-uppercase
                "
              >
                {{ references.statistics.validatedPromisesHistogram }}
              </v-subheader>
              <v-col>
                <SplineChart
                  :chart-data="getValidatedPromisesHistogram"
                />
              </v-col>
            </v-card>
          </v-col>
        </v-row>
        <v-row
          class="mt-0"
        >
          <v-col
            class="pt-2"
          >
            <v-card
              elevation="0"
              class="pt-5 pa-4"
            >
              <v-row>
                <v-col
                  cols="12"
                  md="4"
                  style="height: 492px;"
                >
                  <v-subheader
                    class="
                      align-start
                      text-uppercase
                    "
                  >
                    {{ references.statistics.offerStatus }}
                  </v-subheader>
                  <BarChart
                    :chart-data="getOfferStatus"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                  style="height: 492px;"
                >
                  <v-subheader
                    class="
                      align-start
                      text-uppercase
                    "
                  >
                    {{ references.statistics.applicationSource }}
                  </v-subheader>
                  <PieChart
                    :chart-data="getApplicationSource"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                  style="height: 492px;"
                >
                  <v-subheader
                    class="
                      align-start
                      text-uppercase
                    "
                  >
                    {{ references.statistics.offersVsTotalApplications }}
                  </v-subheader>
                  <BarChart
                    :chart-data="totalOffersVsTotalApplications"
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </v-col>
    <v-col
      cols="12"
      md="3"
      class="d-flex flex-column md-and-up-sticky static"
      order-md="3"
      order="2"
      style="gap: 8px;"
      :style="`top: ${focusModeToolbarTop}; height: fit-content;`"
    >
      <template v-if="isLoading">
        <v-skeleton-loader
          type="card-heading, divider, list-item@15"
        />
      </template>
      <template v-else-if="dataIsReady">
        <List
          :title="references.statistics.recruiterStatus"
          :items="getRecruiterStatus"
        />
      </template>
    </v-col>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import List from '@/components/charts/List.vue';
import BarChart from '@/components/charts/BarChart.vue';
import PieChart from '@/components/charts/PieChart.vue';
import SplineChart from '@/components/charts/SplineChart.vue';

export default {
  name: 'Statistics',
  components: { List, BarChart, PieChart, SplineChart },
  props: {
    application: {
      type: Object,
      default: () => ({})
    },
    offer: {
      type: Object,
      default: () => ({})
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState(['references']),
    dataIsReady () {
      return Boolean(
        this.application?.totalApplications?.values && this.offer?.offers?.values
      );
    },
    totalApplications () {
      const { values, labels, ...restData } = this.application.totalApplications;
      const { value } = values[0];

      return {
        ...restData,
        title: value,
        subtitle: this.getLabel({ isSingular: value === 1, labels }),
        labels, // Used at totalOffersVsTotalApplications
        to: { name: 'candidatures' }
      };
    },
    totalCanceledApplications () {
      const { values, labels, ...restData } = this.application.totalCanceledApplications;
      const { value } = values[0];

      return {
        ...restData,
        title: value,
        subtitle: this.getLabel({ isSingular: value === 1, labels }),
        to: { name: 'candidatures', query: { canceled: true } }
      };
    },
    totalOffers () {
      const { values, labels, ...restData } = this.offer.offers;

      return {
        ...restData,
        title: values.length,
        subtitle: this.getLabel({ isSingular: values.length === 1, labels }),
        labels, // Used at totalOffersVsTotalApplications
        to: { name: 'offers' }
      };
    },
    totalOffersWithoutApplications () {
      const { values, labels, ...restData } = this.application.offersWithApplications;
      const offersWithApps = values.map(app => app.value);

      // Get all offer uuids
      const allOffers = this.offer.offers.values.map(offer => offer.value);

      // Calculate offers without applications
      const withoutApplications = allOffers.filter(offerId => !offersWithApps.includes(offerId)).length;

      return {
        ...restData,
        title: withoutApplications,
        subtitle: this.getLabel({ isSingular: withoutApplications === 1, labels }),
        to: { name: 'offers', query: { showOffers: 'sans-candidature' } }
      };
    },
    averageTimeToApplyOffer () {
      const { values, ...restData } = this.application.totalAverage;
      const [value] = values;

      return {
        ...restData,
        title: this.formatDuration(value.value)
      };
    },
    getTopOffersWithApplications () {
      const { values, ...restData } = this.application.topOffersWithApplications;

      return values.map(offer => ({
        ...restData,
        title: offer.name,
        count: offer.count,
        to: {
          name: 'offer',
          params: {
            contentTypeLabel: 'offres',
            mode: 'vue',
            uuid: offer.uuid
          }
        }
      }));
    },
    getRecruiterStatus () {
      const { values, ...restData } = this.application.recruiterStatus;

      return values.map(status => ({
        ...restData,
        title: this.references.recruiterStatus[status.value],
        count: status.count,
        to: {
          name: 'candidatures',
          query: { recruiterStatus: status.value }
        }
      }));
    },
    getOfferStatus () {
      const { values, publishedLabels, draftedLabels, publishedColor, draftedColor } = this.offer.status;

      if (!values.length) {
        return {
          values: [
            {
              name: draftedLabels[0],
              count: 0,
              color: this.vuetifyColors[publishedColor]
            },
            {
              name: publishedLabels[0],
              count: 0,
              color: this.vuetifyColors[draftedColor]
            }
          ]
        };
      }

      return {
        values: values.map(status => {
          const { value, count } = status;
          return {
            to: { name: 'offers', query: { status: value } },
            name: value === 'published'
              ? this.getLabel({ isSingular: count === 1, labels: publishedLabels })
              : this.getLabel({ isSingular: count === 1, labels: draftedLabels }),
            count: count,
            color: value === 'published'
              ? this.vuetifyColors[publishedColor]
              : this.vuetifyColors[draftedColor]
          };
        })
      };
    },
    totalOffersVsTotalApplications () {
      const { title: totalApplications, labels: applicationlabels } = this.totalApplications;
      const { title: totalOffers, labels: offerlabels } = this.totalOffers;

      return {
        values: [
          {
            to: { name: 'offers' },
            name: this.getLabel({ isSingular: totalOffers <= 1, labels: offerlabels }),
            count: totalOffers
          },
          {
            to: { name: 'candidatures' },
            name: this.getLabel({ isSingular: totalApplications <= 1, labels: applicationlabels }),
            count: totalApplications
          }
        ]
      };
    },
    getApplicationSource () {
      const { values } = this.application.source || {};

      if (!values.length) {
        return {
          values: [{
            value: 'UCPA',
            count: 0,
            color: this.vuetifyColors.primary
          }]
        };
      }

      return {
        values: values.map(source => ({
          to: { name: 'candidatures', query: { source: source.value } },
          value: source.value === 'Broadbean Demo Board' ? 'DEMO' : source.value.toUpperCase(),
          count: source.count,
          color: source.value === 'ucpa' ? this.vuetifyColors.primary : null
        }))
      };
    },
    getValidatedPromisesHistogram () {
      const { values } = this.application.validatedPromisesHistogram;

      return {
        values,
        pointsColor: this.vuetifyColors.primary,
        lineColor: this.vuetifyColors.lightGreen
      };
    },
    vuetifyColors () {
      return this.$vuetify.theme.themes.light;
    },
    focusModeToolbarTop () {
      return this.focusMode ? '24px' : '76px';
    }
  },
  methods: {
    getLabel ({ isSingular, labels }) {
      return labels[isSingular ? 0 : 1];
    },
    formatDuration (days) {
      if (!days) return 'N/A';

      const wholeDays = Math.floor(days);
      const hours = Math.round((days - wholeDays) * 24);

      if (wholeDays === 0) {
        return `${hours} heure${hours > 1 ? 's' : ''}`;
      }

      if (hours === 0) {
        return `${wholeDays} jour${wholeDays > 1 ? 's' : ''}`;
      }

      return `${wholeDays} jour${wholeDays > 1 ? 's' : ''} et ${hours} heure${hours > 1 ? 's' : ''}`;
    }
  }
};
</script>
